/**
 * Dark-app Suchfeld — natives Browser-Clear (X) aus, Token-Clear statt dessen.
 * Liefert die Browser-Chrome-Bereinigung mit der Komponente aus.
 *
 * @atom Atom
 */

import React, { useId, type CSSProperties, type Ref } from 'react'
import { Search, X } from 'lucide-react'
import './KwSearchField.css'
import { color, font, radius } from '../tokens'

export type KwSearchFieldProps = {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  'aria-label': string
  id?: string
  autoComplete?: string
  autoFocus?: boolean
  inputRef?: Ref<HTMLInputElement>
  /** Leading Lucide Search. Default true. */
  showIcon?: boolean
  /** Hide the clear action when the host supplies one close action for the whole search. */
  showClear?: boolean
  /** Wrapper (Pill / Bar). */
  style?: CSSProperties
  inputStyle?: CSSProperties
  className?: string
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onClick?: React.MouseEventHandler<HTMLInputElement>
}

export function KwSearchField({
  value,
  onChange,
  placeholder = 'Suchen…',
  'aria-label': ariaLabel,
  id: idProp,
  autoComplete = 'off',
  autoFocus,
  inputRef,
  showIcon = true,
  showClear = true,
  style,
  inputStyle,
  className,
  onKeyDown,
  onFocus,
  onBlur,
  onClick,
}: KwSearchFieldProps) {
  const genId = useId()
  const inputId = idProp ?? genId
  const hasValue = value.length > 0

  return (
    <div
      className={['kw-search-field',className].filter(Boolean).join(' ')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 36,
        padding: '0 6px 0 10px',
        borderRadius: radius.lg,
        background: color.darkMid,
        border: `1px solid ${color.darkBorder}`,
        ...style,
      }}
    >
      {showIcon ? (
        <Search
          size={14}
          strokeWidth={2}
          aria-hidden
          style={{ color: color.inkMuted, flexShrink: 0 }}
        />
      ) : null}
      <input
        ref={inputRef}
        id={inputId}
        type="search"
        className="kw-list-search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        onFocus={event => { if (event.currentTarget.dataset.headerToolsRestoringFocus !== 'true') onFocus?.(event) }}
        onBlur={onBlur}
        onClick={onClick}
        style={{
          flex: 1,
          minWidth: 0,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          color: color.white,
          font: `13px/1.3 ${font.sans}`,
          padding: '8px 0',
          ...inputStyle,
        }}
      />
      {showClear && (hasValue ? (
        <button
          type="button"
          aria-label="Suche leeren"
          onClick={() => {
            onChange('')
            const el =
              typeof inputRef === 'object' && inputRef && 'current' in inputRef
                ? inputRef.current
                : document.getElementById(inputId)
            if (el instanceof HTMLInputElement) el.focus()
          }}
          style={{
            width: 28,
            height: 28,
            flexShrink: 0,
            border: 'none',
            borderRadius: radius.md,
            display: 'grid',
            placeItems: 'center',
            background: 'transparent',
            color: color.inkMuted,
            cursor: 'pointer',
            padding: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = color.whiteAlpha(0.08)
            e.currentTarget.style.color = color.white
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent'
            e.currentTarget.style.color = color.inkMuted
          }}
        >
          <X size={14} strokeWidth={2} aria-hidden />
        </button>
      ) : (
        <span aria-hidden style={{ width: 28, height: 28, flexShrink: 0 }} />
      ))}
    </div>
  )
}
